<template>
  <!-- Root: `v-click-outside` custom directive closes the dropdown whenever a click lands outside this element -->
  <div v-click-outside="newCloseDropdown" class="new-base-dropdown">
    <!-- Optional label above the dropdown, only rendered when `label` prop is set -->
    <span v-if="label" class="new-base-dropdown__label">{{ label }}</span>
    <!-- Clickable head/trigger: toggles the options panel; `classActive` highlights it while open -->
    <div
      class="new-base-dropdown__head new-head"
      :class="classActive"
      @click="newToggleDropdown()"
    >
      <div class="new-head__inner">
        <!-- Shows the selected option's label, or a "no sorting" placeholder when nothing is selected (modelValue === -1) -->
        <span
          class="new-base-dropdown__title"
          :class="{ 'new-base-dropdown__title_selected': getValue !== -1 }"
        >
          {{ getValue === -1 ? "Без сортировки" : (safeOptions[getValue]?.label) }}
        </span>
        <!-- Chevron/arrow icon; class is caller-supplied via `iconClass` prop and rotates via CSS when active -->
        <span class="new-base-dropdown__icon" :class="iconClass" />
      </div>
    </div>
    <!-- Options panel, only rendered while `isOptionVisible` is true -->
    <div v-if="isOptionVisible" class="new-base-dropdown__items">
      <div class="new-base-dropdown__wrapper">
        <!-- One row per entry in `safeOptions`; clicking selects it via `newSelectOption` -->
        <div
          v-for="(option, i) in safeOptions"
          :key="option?.label || i"
          class="new-base-dropdown__item new-item"
          @click="newSelectOption(i)"
        >
          <div class="new-item__inner">
            <span class="new-base-dropdown__title">
              {{ option?.label }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { directive as vClickOutside } from "click-outside-vue3";

/**
 * @emits selected - Fired with the index of the option the user clicked.
 */
const emit = defineEmits(["selected"]);
/**
 * @prop {Number} modelValue - Index of the currently selected option in `options` (-1 means "no selection / no sorting").
 * @prop {Array} options - List of selectable option objects, each expected to have at least a `label`.
 * @prop {String} active - Unused external "active" flag/identifier, passed through for caller bookkeeping.
 * @prop {String} mode - Unused mode flag reserved for caller-specific behavior variants.
 * @prop {String} iconClass - CSS class applied to the trigger's chevron/arrow icon.
 * @prop {String} label - Optional label rendered above the dropdown.
 */
const props = defineProps({
  modelValue: {
    type: Number,
    default: -1,
  },
  options: {
    type: Array,
    default: () => [],
    // Ensure options are objects with a label (string)
    validator: /** @param {unknown[]} arr */ (arr) => Array.isArray(arr) && arr.every(o => o && typeof o === 'object' && 'label' in o),
  },
  active: {
    type: String,
    default: "",
  },
  mode: {
    type: String,
    default: "",
  },
  iconClass: {
    type: String,
    default: "",
  },
  label: {
    type: String,
    default: "",
  },
});

/** Whether the options panel is currently open/visible. */
const isOptionVisible = ref(false);

/** Class object toggling the "active" (open) visual state on the dropdown head. */
const classActive = computed(() => {
  return [
    {
      "new-base-dropdown__head_active": isOptionVisible.value === true,
    },
  ];
});

/** Mirrors `props.modelValue` for convenient read access in the template. */
const getValue = computed(() => props.modelValue);
// Coerce options to an array of objects with label
/** Defensive copy of `props.options` guaranteed to be an array, even if a bad value is passed in. */
const safeOptions = computed(() => Array.isArray(props.options) ? /** @type {{label?: string}[]} */ (props.options) : []);

/** Closes the options panel; bound to the `v-click-outside` directive so an outside click dismisses it. */
const newCloseDropdown = () => {
  isOptionVisible.value = false;
};
/**
 * Selects an option by index: emits `selected` with that index to the parent
 * (which owns `modelValue`) and closes the panel.
 * @param {number} i - Index of the clicked option within `safeOptions`.
 */
const newSelectOption = (i) => {
  emit("selected", i);
  isOptionVisible.value = false;
};
/** Toggles the options panel open/closed when the head is clicked. */
const newToggleDropdown = () => {
  isOptionVisible.value = !isOptionVisible.value;
};
</script>

<style lang="scss" scoped>
.new-base-dropdown {
  position: relative;
  width: 100%;
  color: rgba(39, 39, 39, 0.3);
  font-size: 20px;
  &__label {
    /* OLD: color: #272727; */
    color: var(--color-grey-tertiary);
    font-size: 16px;
  }
  &__title {
    &_selected {
      /* OLD: color: #272727; */
      color: var(--color-grey-tertiary);
    }
  }
  &__head {
    position: relative;
    display: flex;
    align-items: center;
    padding: 10px 15px;
    border: 1px solid rgba(23, 23, 25, 0.1);
    border-radius: 10px;
    cursor: pointer;
    transition: 0.2s ease-in-out;
    &_active {
      background: rgba(197, 228, 249, 0.3);
      /* OLD: border-color: #1390e5; */
      border-color: var(--color-accent-primary);
    }
    &_active .new-base-dropdown__icon:before {
      /* OLD: color: #d1d1d1; */
      color: var(--color-form-input-border);
    }
    &_active .new-base-dropdown__icon {
      transform: rotate(180deg);
    }
  }
  .new-head {
    &__inner {
      display: flex;
      align-items: center;
      width: 100%;
    }
  }
  &__wrapper {
    overflow-y: auto;
    max-height: 170px;
  }
  &__items {
    position: absolute;
    top: 82px;
    display: flex;
    width: 100%;
    padding: 0 12px;
    flex-direction: column;
    background: var(--color-app-card-bg);
    color: var(--color-foreground-primary);
    box-shadow: 0 25px 35px rgba(70, 70, 76, 0.19);
    border-radius: 10px;
    z-index: 100;
  }
  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 7px;
    margin: 5px 0;
    padding: 10px;
    cursor: pointer;
    transition: 0.2s ease-in-out;
    &:hover {
      background: var(--color-app-card-bg-hover);
    }
  }
  .new-item {
    &__inner {
      display: flex;
      align-items: center;
    }
  }
  &__icon {
    position: absolute;
    right: 15px;
    transition: 0.2s ease-in-out;
    &:before {
      font-size: 14px;
      /* OLD: color: #d1d1d1; */
      color: var(--color-form-input-border);
      transition: 0.2s ease-in-out;
    }
  }
  @include _480 {
    font-size: 16px;
  }
  @include _380 {
    font-size: 16px;
  }
}
</style>
